import { useMemo } from 'react';
import { useField } from 'formik';
import { twMerge } from 'tailwind-merge';
import {
  Checkbox,
  InputLabel,
  FormControl,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils';
import { SelectFormField } from 'types';
import { defaultLanguage } from 'context';
import { useCourseContext } from 'context';

import { Icon } from '../icons';

export const FormSelectCorrectField = ({
  id,
  label,
  options,
  disabled,
  translations,
}: SelectFormField & { disabled?: boolean }) => {
  const { language } = useCourseContext();

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        label,
        options: options?.options
          ? options?.options.map((value) => ({ value, label: value }))
          : [],
      };
    }

    const translation = translations[language];
    const langOptions = translation.options || options?.options;
    return {
      label: label ? translation?.label || label : label,
      options: langOptions
        ? langOptions.map((value, index) => ({
            label: value,
            value: options?.options?.[index] || value,
          }))
        : [],
    };
  }, [label, options?.options, language, translations]);

  const { multiple, correctOptions } = options ?? {};
  const isMultiple = multiple ?? false;
  const [field, meta, { setValue }] = useField(id);

  const fieldValue: any = field.value ?? (isMultiple ? [] : '');

  const errorText = controlError(meta, id, label);
  const hasError = Boolean(errorText);

  return (
    <FormControl error={hasError} className="flex flex-col gap-2">
      {translation.label && (
        <InputLabel className="mb-2" sx={{ whiteSpace: 'pre-line' }}>
          {translation.label}
        </InputLabel>
      )}
      {translation.options.map(({ value, label }, index) => {
        const isChecked = isMultiple
          ? fieldValue.includes(value)
          : fieldValue === value;
        let isWrong = false;
        let isCorrect = false;
        if (disabled && correctOptions) {
          const isCorrectOption = correctOptions.includes(index);
          isWrong = isChecked && !isCorrectOption;
          isCorrect = isCorrectOption;
        }

        let bgClass = 'bg-gray-primary';
        if (isWrong) {
          bgClass = 'bg-red-primary-primary';
        } else if (isChecked || isCorrect) {
          bgClass = 'bg-blue-primary';
        }

        return (
          <div key={value} className={twMerge('rounded-lg', bgClass)}>
            <div className="m-[1.5px] rounded-[6.5px] bg-white">
              <FormControlLabel
                label={label}
                disabled={disabled}
                className="items-start p-3"
                control={
                  <Checkbox
                    id={value}
                    disabled={disabled}
                    checked={isChecked}
                    className="h-6 w-6"
                    icon={
                      <div className="relative">
                        <Icon.CheckboxOff className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                    }
                    checkedIcon={
                      <div className="relative">
                        <Icon.CheckboxOn className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                    }
                    onChange={(event) => {
                      const newValue = event.target.checked;
                      if (isMultiple) {
                        const oldValues = new Set(fieldValue);
                        if (newValue) {
                          oldValues.add(value);
                        } else {
                          oldValues.delete(value);
                        }
                        setValue(Array.from(oldValues), true);
                      } else {
                        setValue(newValue ? value : '', true);
                      }
                    }}
                  />
                }
              />
            </div>
          </div>
        );
      })}
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
